/* @flow */

import {
  getTopicId,
  getTopicBookableURL,
  getTopicModerationURL,
  normalizeTopic,
  normalizeTopics,
} from '@e180/domain/lib/topic/util';
import {
  topicModerationPendingStatus,
  topicModerationRejectedStatus,
  topicModerationVerifiedStatus,
} from '@e180/domain/lib/topic/setting';
import { assertNumber, assertObject } from '@e180/util/lib/assert';

import * as namespaces from './topicActionNamespaces';
import * as types from './topicActionTypes';
import { parseResponse } from 'src/shared/app/base/util/ajaxUtils';
import * as marketNamespaces from 'src/shared/app/market/action/marketActionNamespaces';
import { patchEntities } from 'src/shared/domain/base/action/entityActions';
import { fetchKeywords } from 'src/shared/domain/keyword/action/keywordActions';
import {
  topicEndpoint,
  topicsEndpoint,
} from 'src/shared/app/base/settings/endpointSettings';

import type { Topic } from '@e180/domain/lib/topic/type';

// All topics

export function simulateTopicsFetch(namespace?: string) {
  return {
    type: types.SIMULATE_TOPICS_FETCH,
    namespace,
  };
}

export function fetchAllTopics(eventId: number, params?: Object) {
  const obj =
    params && typeof params === 'object'
      ? {
          ...params,
          visible: true,
          past: false,
          spots_left: true,
          for_marketplace: 1,
        }
      : {
          visible: true,
          past: false,
          spots_left: true,
          for_marketplace: 1,
        };

  return fetchTopics(eventId, obj, namespaces.ALL_TOPICS);
}

export function fetchAllVerifiedTopics(eventId: number, params?: Object) {
  const obj =
    params && typeof params === 'object'
      ? {
          ...params,
          moderation: topicModerationVerifiedStatus,
        }
      : {
          moderation: topicModerationVerifiedStatus,
        };

  return fetchAllTopics(eventId, obj);
}

export function fetchUserTopics(
  eventId: number,
  userId: number,
  params?: Object,
) {
  const obj =
    params && typeof params === 'object'
      ? {
          ...params,
          author_id: userId,
          visible: true,
          moderation: topicModerationVerifiedStatus,
        }
      : {
          author_id: userId,
          visible: true,
          moderation: topicModerationVerifiedStatus,
        };

  return fetchTopics(eventId, obj, namespaces.USER_TOPICS);
}

export function fetchOwnTopics(eventId: number, params?: Object) {
  const visible = params ? params.visible : null;

  const obj =
    params && typeof params === 'object'
      ? {
          ...params,
          visible: !(visible === false),
          author_id: 'me',
        }
      : {
          author_id: 'me',
          visible: !(visible === false),
        };

  return fetchTopics(eventId, obj, namespaces.OWN_TOPICS);
}

export function fetchUserRelatedTopics(eventId: number, userId: number) {
  const params = {
    related_user: userId,
    limit: 5,
    moderation: topicModerationVerifiedStatus,
  };

  return fetchTopics(eventId, params, namespaces.USER_RELATED_TOPICS);
}

export function fetchOwnActiveTopics(eventId: number) {
  const params = {
    ordering: '-date_created',
    visible: true,
    author_id: 'me',
    moderation: topicModerationVerifiedStatus,
  };

  return fetchTopics(eventId, params, namespaces.OWN_ACTIVE_TOPICS);
}

export function fetchOwnHiddenTopics(eventId: number) {
  const params = {
    ordering: '-date_created',
    visible: false,
    author_id: 'me',
  };

  return fetchTopics(eventId, params, namespaces.OWN_HIDDEN_TOPICS);
}

export function fetchOwnPendingTopics(eventId: number) {
  const params = {
    ordering: '-date_created',
    author_id: 'me',
    moderation: topicModerationPendingStatus,
  };

  return fetchTopics(eventId, params, namespaces.OWN_PENDING_TOPICS);
}

export function fetchOwnRejectedTopics(eventId: number) {
  const params = {
    ordering: '-date_created',
    author_id: 'me',
    moderation: topicModerationRejectedStatus,
  };

  return fetchTopics(eventId, params, namespaces.OWN_REJECTED_TOPICS);
}

export function fetchAllOwnTopics(eventId: number, params?: Object) {
  const obj =
    params && typeof params === 'object'
      ? {
          ...params,
          author_id: 'me',
        }
      : { author_id: 'me' };

  return fetchTopics(eventId, obj, namespaces.OWN_TOPICS);
}

export function fetchTopics(
  eventId: number,
  params?: Object,
  namespace?: string,
) {
  return (dispatch: any, { get }: Object) => {
    dispatch(fetchTopicsRequest(namespace));

    return get(topicsEndpoint(eventId), params)
      .then(parseResponse)
      .then((json) => {
        const { count, next, previous, results } = json;
        const normalizedData = normalizeTopics(results);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        dispatch(
          // $FlowIssue
          fetchTopicsSuccess(result, count, next, previous, namespace, params),
        );

        return json;
      })
      .catch((exception) => {
        dispatch(fetchTopicsFailure(exception, namespace));

        throw exception;
      });
  };
}

export function fetchTopicsRequest(namespace?: string) {
  return {
    type: types.FETCH_TOPICS_REQUEST,
    namespace,
  };
}

export function fetchTopicsSuccess(
  result: Array<Topic>,
  count?: number,
  next?: string,
  previous?: string,
  namespace?: string,
  params?: Object,
) {
  return {
    type: types.FETCH_TOPICS_SUCCESS,
    result,
    count,
    next,
    previous,
    namespace,
    params,
  };
}

export function fetchTopicsFailure(exception: Error, namespace?: string) {
  return {
    type: types.FETCH_TOPICS_FAILURE,
    exception,
    namespace,
  };
}

// Lazy fetch

export function lazyFetchAllTopics(url: string, params?: Object) {
  const obj =
    params && typeof params === 'object'
      ? {
          ...params,
          visible: true,
          past: false,
          spots_left: true,
          for_marketplace: 1,
        }
      : {
          visible: true,
          past: false,
          spots_left: true,
          for_marketplace: 1,
        };

  return lazyFetchTopics(url, obj, namespaces.ALL_TOPICS);
}

export function lazyFetchAllVerifiedTopics(url: string, params?: Object) {
  const obj =
    params && typeof params === 'object'
      ? {
          ...params,
          moderation: topicModerationVerifiedStatus,
        }
      : {
          moderation: topicModerationVerifiedStatus,
        };

  return lazyFetchAllTopics(url, obj);
}

export function lazyFetchTopics(
  url: string,
  params?: Object,
  namespace?: string,
) {
  return (dispatch: any, { get }: Object) => {
    dispatch(lazyFetchTopicsRequest(namespace));

    return get(url, params)
      .then(parseResponse)
      .then((json) => {
        const { next, previous, results } = json;
        const normalizedData = normalizeTopics(results);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        // $FlowIssue
        dispatch(lazyFetchTopicsSuccess(result, next, previous, namespace));
      })
      .catch((e) => {
        dispatch(lazyFetchTopicsFailure(e, namespace));

        throw e;
      });
  };
}

export function lazyFetchTopicsRequest(namespace?: string) {
  return {
    type: types.LAZY_FETCH_TOPICS_REQUEST,
    namespace,
  };
}

export function lazyFetchTopicsSuccess(
  result: Array<Topic>,
  next?: string,
  previous?: string,
  namespace?: string,
) {
  return {
    type: types.LAZY_FETCH_TOPICS_SUCCESS,
    result,
    namespace,
    next,
    previous,
  };
}

export function lazyFetchTopicsFailure(exception: Error, namespace?: string) {
  return {
    type: types.LAZY_FETCH_TOPICS_FAILURE,
    exception,
    namespace,
  };
}

// Single topic

export function simulateTopicFetch() {
  return {
    type: types.SIMULATE_TOPIC_FETCH,
  };
}

export function fetchTopic(id: number) {
  return (dispatch: any, { get }: Object) => {
    dispatch(fetchTopicRequest());

    return get(topicEndpoint(id))
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeTopic(json);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        // $FlowIssue
        dispatch(fetchTopicSuccess(result));

        return json;
      })
      .catch((exception) => {
        dispatch(fetchTopicFailure(exception));

        throw exception;
      });
  };
}

export function fetchTopicRequest() {
  return {
    type: types.FETCH_TOPIC_REQUEST,
  };
}

export function fetchTopicSuccess(result: number) {
  return {
    type: types.FETCH_TOPIC_SUCCESS,
    result,
  };
}

export function fetchTopicFailure(exception: Error) {
  return {
    type: types.FETCH_TOPIC_FAILURE,
    exception,
  };
}

// Create topic

export function createTopic(eventId: number, data: Object) {
  return (dispatch: any, { post }: Object) => {
    dispatch(createTopicRequest());

    return post(topicsEndpoint(eventId), data)
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeTopic(json);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        // $FlowIssue
        dispatch(createTopicSuccess(result));

        return json;
      })
      .catch((e) => {
        dispatch(createTopicFailure(e));

        throw e;
      });
  };
}

export function createTopicRequest() {
  return {
    type: types.CREATE_TOPIC_REQUEST,
  };
}

export function createTopicSuccess(result: number) {
  return {
    type: types.CREATE_TOPIC_SUCCESS,
    result,
  };
}

export function createTopicFailure(exception: Error) {
  return {
    type: types.CREATE_TOPIC_FAILURE,
    exception,
  };
}

// Patch topic

export function patchTopic(topicId: number, data: Object) {
  return (dispatch: any, { patch }: Object) => {
    dispatch(patchTopicRequest());

    return patch(topicEndpoint(topicId), data)
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeTopic(json);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        // $FlowIssue
        dispatch(patchTopicSuccess(result));

        return result;
      })
      .catch((e) => {
        dispatch(patchTopicFailure(e));

        throw e;
      });
  };
}

export function patchTopicRequest() {
  return {
    type: types.PATCH_TOPIC_REQUEST,
  };
}

export function patchTopicSuccess(result: number) {
  return {
    type: types.PATCH_TOPIC_SUCCESS,
    result,
  };
}

export function patchTopicFailure(exception: Error) {
  return {
    type: types.PATCH_TOPIC_FAILURE,
    exception,
  };
}

// Delete topic

export function deleteTopic(topicId: number) {
  return (dispatch: any, { deleteFn }: Object) => {
    dispatch(deleteTopicRequest());

    return deleteFn(topicEndpoint(topicId))
      .then(parseResponse)
      .catch((e) => {
        dispatch(deleteTopicFailure(e));

        throw e;
      });
  };
}

export function deleteTopicRequest() {
  return {
    type: types.DELETE_TOPIC_REQUEST,
  };
}

export function deleteTopicSuccess(result: number) {
  return {
    type: types.DELETE_TOPIC_SUCCESS,
    result,
  };
}

export function deleteTopicFailure(exception: Error) {
  return {
    type: types.DELETE_TOPIC_FAILURE,
    exception,
  };
}

// Bookmark topic

export function bookmarkTopic(url: string, bookmarked: boolean) {
  return (dispatch: any, { post }: Object) => {
    dispatch(bookmarkTopicRequest());

    return post(url, { bookmarked })
      .then(parseResponse)
      .then((json) => {
        const { id, bookmarked } = json;

        dispatch(bookmarkTopicSuccess(id, bookmarked));

        return bookmarked;
      })
      .catch((e) => {
        dispatch(bookmarkTopicFailure(e));

        throw e;
      });
  };
}

export function bookmarkTopicRequest() {
  return {
    type: types.BOOKMARK_TOPIC_REQUEST,
  };
}

export function bookmarkTopicSuccess(id: number, bookmarked: boolean) {
  return {
    type: types.BOOKMARK_TOPIC_SUCCESS,
    id,
    bookmarked,
  };
}

export function bookmarkTopicFailure(exception: Error) {
  return {
    type: types.BOOKMARK_TOPIC_FAILURE,
    exception,
  };
}

// Topic is bookable?

export function fetchIsTopicBookable(topic: Topic) {
  return (dispatch: any, { smartGet }: Object) => {
    dispatch(fetchIsTopicBookableRequest());

    return smartGet(getTopicBookableURL(topic))
      .then((json) => {
        const { can_join: bookable, message: unbookableReason } = json;
        dispatch(
          fetchIsTopicBookableSuccess(
            bookable,
            unbookableReason,
            getTopicId(topic),
          ),
        );
      })
      .catch((exception) => {
        dispatch(fetchIsTopicBookableFailure(exception));
      });
  };
}

export function fetchIsTopicBookableRequest() {
  return {
    type: types.FETCH_IS_TOPIC_BOOKABLE_REQUEST,
  };
}

export function fetchIsTopicBookableSuccess(
  bookable: boolean,
  unbookableReason: string,
  id: number,
) {
  return {
    type: types.FETCH_IS_TOPIC_BOOKABLE_SUCCESS,
    bookable,
    unbookableReason,
    id,
  };
}

export function fetchIsTopicBookableFailure(exception: Error) {
  return {
    type: types.FETCH_IS_TOPIC_BOOKABLE_FALIURE,
    exception,
  };
}

// Topic Moderation rejected message

export function patchTopicModeration(data: Object) {
  return {
    type: types.PATCH_TOPIC_MODERATION,
    data,
  };
}

export function fetchTopicModerationReason(topic: Topic) {
  return (dispatch: any, { smartGet }: Object) => {
    dispatch(fetchTopicModerationReasonRequest());

    return smartGet(getTopicModerationURL(topic))
      .then((json) => {
        let moderationReason = '';

        if (json.length) {
          moderationReason = json[0].reason;
        }
        dispatch(
          fetchTopicModerationReasonSuccess(
            moderationReason,
            getTopicId(topic),
          ),
        );
      })
      .catch((exception) => {
        dispatch(fetchTopicModerationReasonFailure(exception));
      });
  };
}

export function fetchTopicModerationReasonRequest() {
  return {
    type: types.FETCH_TOPIC_MODERATION_REASON_REQUEST,
  };
}

export function fetchTopicModerationReasonSuccess(
  moderationReason: string,
  id: number,
) {
  return {
    type: types.FETCH_TOPIC_MODERATION_REASON_SUCCESS,
    moderationReason,
    id,
  };
}

export function fetchTopicModerationReasonFailure(exception: Error) {
  return {
    type: types.FETCH_TOPIC_MODERATION_REASON_FAILURE,
    exception,
  };
}

/*
 |------------------------------------------------------------------------------
 | Keywords
 |------------------------------------------------------------------------------
 */

export function fetchTopicKeywords(
  eventId: number,
  options: Object,
  namespace?: string = marketNamespaces.MARKET_AUTOCOMPLETE_TOPICS,
) {
  assertNumber(eventId, 'eventId');
  assertObject(options, 'options');

  const { query, threshold, exclude, topic_context: context } = options;

  const data = {
    q: query || '',
    topics_count: threshold || 1,
    others: exclude || '',
    topic_context: context || '',
  };

  return fetchKeywords(eventId, data, namespace);
}
